import React, { FC, useEffect, useRef } from 'react'
import { TextBox, TextBoxProps } from '@ds/ui'
import { mergeRefs, useTimer } from '@ds/react-utils'
import {
  getRelativePosition,
  getAbsolutePosition,
} from '../FormattedTextBox/formatters/utils/transposeCursorPosition'

// TODO: Build PhoneInput with the public FormattedTextBox and get rid of this one.

/**

    We make the assumption that every chracter in the value is either a formatting character or a
    signficant character. The significant value is what we get after removing all formatting
    characters. Given a phone number, such as (612) 123-4567, the definition of a significant character
    is any number. Everything else is a formatting character.

    Formatted Value:     (612) 123-4567
    Significant Value:   6121234567

    We need to know the cursor position relative to significant characters. In the following examples the
    cursor is represented with a pipe (|).

    Formatted Value:               (612) 123|-4567
    Significant Value:             612123|4567
    Significant Cursor Position:   6

    When the user makes a change that affects the significant value we need to store the cursor position
    from the onChange event. For example, if they press backspace...

    value                         (612) 123|-4567
    onChange value:               (612) 12|-4567
    Significant Cursor Position:  5

    The parent component may do some formatting before supplying a new value. Regardless of formatting the
    significant cursor position should still be 5.

    value                         (612) 12|4-567

    The same technique works selecting and deleting muiltiple characters, adding one or more new characters,
    or selecting and replacing characters.

    This techique won't work in the case that the formatter adds significant characters before the cursor
    position. For example, a currency formatter might add a zero before the decimal. In those situations
    the parent component can supply a number as the return value of onChangeText, which will be used as
    a cursor adjustment. So when adding a 0 to the beginning of the value you would bump the cursor forward
    one position: Return the number 1.

    ---------------------

    Sometimes the user will make a change that doesn't affect the significant value. If the significant value is
    unchanged we won't call onChangeText, and the parent remains unaware that anything happened. We still have to
    manage the cursor position to prevent it from jumping to the end of the input. First, its helpful to catalog
    the scenarios where this can happen, along with the intended result:

    The cursor is before a formatting char and the same char is typed    ---->    cursor position += 1
    The cursor is before a formatting char and user presses delete       ---->    cursor position += 1
    The cursor is after a formatting char and user presses backspace     ---->    cursor position -= 1
    Any other action that doesn't change the significant value           ---->    no change

    For the last scenario, where we intend to do nothing, we can call event.preventDefault() inside the onKeyPress
    event. That stops the cursor from jumping to the end of the input.

    For the other three scenarios we need to know what key was pressed and where the cursor was beforehand. That
    information is provided in the onKeyDown event.

 */

type FormattingCharTest = (character: string, index: number) => boolean

type AcceptedTest = (
  character: string,
  context: { selectionStart: number | null; selectionEnd: number | null }
) => boolean

type CursorAdjustment = number

export interface FormattedTextBoxProps extends TextBoxProps {
  /** Determines when a character should be treated as a formatting character */
  isFormattingChar: FormattingCharTest
  /** Determines which characters should be ignored completely */
  accept?: AcceptedTest
  /** A formatted string */
  value: string
  /** Will fire only when non-formatting characters have been changed */
  onChangeText: (
    value: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => CursorAdjustment | void
}

/**
 * Manages the cursor position in a text box with a formatted value.
 *
 * @param props
 */
export const FormattedTextBox: FC<FormattedTextBoxProps> = ({
  isFormattingChar,
  accept,
  value,
  onChangeText,
  forwardedRef,
  ...restProps
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const significantCursorPosition = useRef<number | null>(null)
  const keyDown = useRef<string | null>(null)
  const prevCursorPosition = useRef<number | null>(null)
  const { runLater: deferSetSelection } = useTimer()

  useEffect(() => {
    const significantPosition = significantCursorPosition.current
    const inputElement = inputRef.current

    if (significantPosition !== null && inputElement) {
      const formattedPosition = getAbsolutePosition(
        value,
        (char, index) => !isFormattingChar(char, index),
        Infinity,
        significantPosition
      )
      inputElement.setSelectionRange(formattedPosition, formattedPosition)
    }
  }, [value, isFormattingChar])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event
    const { selectionStart, value: eventValue } = target

    const significantValue = removeFormatting(isFormattingChar, value)
    const significantInput = removeFormatting(isFormattingChar, eventValue)

    if (selectionStart === null) {
      if (significantValue !== significantInput) {
        significantCursorPosition.current = significantInput.length
        onChangeText(significantInput, event)
      }
      return
    }

    if (significantValue === significantInput) {
      let nextCursorPosition = prevCursorPosition.current
      if (keyDown.current === 'Delete' || keyDown.current === 'Backspace') {
        nextCursorPosition = selectionStart
      }
      if (keyDown.current === value[selectionStart - 1]) {
        nextCursorPosition = selectionStart
      }
      if (nextCursorPosition !== null) {
        const cp = nextCursorPosition
        deferSetSelection(() => target.setSelectionRange(cp, cp))
      }
      return
    }

    significantCursorPosition.current = getRelativePosition(
      eventValue,
      (char, index) => !isFormattingChar(char, index),
      Infinity,
      selectionStart
    )

    const cursorAdjustment = onChangeText(significantInput, event)
    if (cursorAdjustment) {
      significantCursorPosition.current += cursorAdjustment
    }
  }

  return (
    <TextBox
      {...restProps}
      forwardedRef={mergeRefs(inputRef, forwardedRef)}
      onKeyDown={(event) => {
        const { key } = event
        const target = event.target as HTMLInputElement
        const { selectionStart } = target

        keyDown.current = key
        prevCursorPosition.current = selectionStart
      }}
      onKeyPress={(event) => {
        const { key } = event
        const target = event.target as HTMLInputElement
        const { selectionStart, selectionEnd } = target

        if (
          accept &&
          key !== 'Enter' &&
          !accept(key, { selectionStart, selectionEnd })
        ) {
          event.preventDefault()
        }
      }}
      onChange={handleChange}
      value={value}
    />
  )
}

/**
 * Returns true if the significant value has changed.
 *
 * @param isFormattingChar - Determines whether a character is for formatting
 * @param previousValue - A string which can have both significant and formatting characters
 * @param nextValue - A string which can have both significant and formatting characters
 */
export const removeFormatting = (
  isFormattingChar: FormattingCharTest,
  value: string
) => {
  let significantValue = ''

  for (let i = 0; i < value.length; i++) {
    if (!isFormattingChar(value[i], i)) {
      significantValue += value[i]
    }
  }

  return significantValue
}
